import { useMemo, useState } from "react";
import { Check, Pencil, Plus, Ruler, Trash2, X } from "lucide-react";
import { useTakeoff } from "./store";
import { pageKey } from "./pageState";
import {
  FIXED_LABELS, STANDARD_SCALES, SIZES, parseSizeThickness,
  computeEstimate, lineValue, ductSqFt, money, fmtQty,
} from "./pricing";
import { CARD, INPUT_SM, LABEL, MONO, BTN_ORANGE } from "./theme";
import { fmtFtIn, uid } from "./geometry";
import { PipeBuilderDialog, DuctBuilderDialog, CustomItemDialog, LibraryItemDialog, ConfirmDialog } from "./dialogs";
import OverlayPanel from "./OverlayPanel";

/** Pipes sort numerically smallest→largest (10/12/14/16 after 9), then by
 * name; non-pipe items keep stable order after. */
export const sortJobItems = (items) =>
  [...items].sort((a, b) => {
    const ia = SIZES.indexOf(parseSizeThickness(a.name)[0]);
    const ib = SIZES.indexOf(parseSizeThickness(b.name)[0]);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      if (ia !== ib) return ia - ib;
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    }
    return 0;
  });

export default function TakeoffPanel({
  drawing, pageNum, pageState, pageItems, measurable,
  activeItemId, onPickItem, selection, setSelection,
  applyPreset, startCustomScale, overlay, setOverlay, pageText,
}) {
  const { doc, mutate, jobItems, library, addJobItem, updateJobItem, removeJobItem, viewer } = useTakeoff();
  const [pipeOpen, setPipeOpen] = useState(false);
  const [ductOpen, setDuctOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [removeItem, setRemoveItem] = useState(null);
  const [labelInput, setLabelInput] = useState("");
  const [mult, setMult] = useState("");

  const overrides = useMemo(() => ({
    pvc: doc?.pvcPriceOverrides || {}, duct: doc?.ductPriceOverrides || {}, support: doc?.supportPriceOverrides || {},
  }), [doc]);

  const estimate = useMemo(
    () => (doc ? computeEstimate(doc.items || [], doc.manualEstimateItems || [], overrides) : null),
    [doc, overrides]
  );

  const sortedItems = useMemo(() => sortJobItems(jobItems), [jobItems]);
  const activeItem = jobItems.find((i) => i.item_id === activeItemId);

  // A quick custom item: created in this job, not linked to the library.
  const isCustomItem = (it) => it && !it.libraryId && !it.library_id && !it.ductSpec && !parseSizeThickness(it.name || "")[0];

  // Quick item save — routes the price where the user chose. "extra:new:"
  // creates the extra first (label optional — auto-numbered) so the price
  // can land in it; editing re-points the same item without duplicating it.
  const saveCustomItem = async (item) => {
    let priceTo = item.priceTo || "pipe";
    if (priceTo.startsWith("extra:new:")) {
      const label = priceTo.slice(10);
      const existing = doc?.extras || [];
      const id = uid();
      const name = label || `Extra ${existing.length + 1}`;
      mutate((d) => {
        d.extras = d.extras || [];
        d.extras.push({ id, name, createdAt: Date.now(), updatedAt: Date.now(), groups: [] });
      });
      priceTo = `extra:${id}`;
    }
    const payload = { ...item, priceTo };
    if (!customOpen && editItem) await updateJobItem(editItem.item_id, payload);
    else await addJobItem(payload);
  };

  // Job-wide measured totals per palette item (summed across every sheet,
  // multipliers applied) — recomputed on any doc change so it stays live.
  const tagTotals = useMemo(() => {
    const m = {};
    for (const it of doc?.items || []) {
      if (!it.tagId) continue;
      const mult = Math.max(1, Math.round(it.multiplier || 1));
      const q = (it.quantity || 0) * mult;
      const t = m[it.tagId] || (m[it.tagId] = { ft: 0, sqft: 0, count: 0 });
      if (it.type === "linear") t.ft += q;
      else if (it.type === "area") t.sqft += q;
      else t.count += q;
    }
    return m;
  }, [doc]);

  const tagTotalText = (t) => {
    if (!t) return null;
    if (t.ft > 0) return fmtFtIn(t.ft);
    if (t.sqft > 0) return `${fmtQty(t.sqft)} sq ft`;
    if (t.count > 0) return `${fmtQty(t.count)} ea`;
    return null;
  };

  const selectedItems = useMemo(
    () => (doc?.items || []).filter((i) => selection.includes(i.id)),
    [doc, selection]
  );

  const setPageName = (name) => mutate((d) => {
    const key = pageKey(drawing.drawing_id, pageNum);
    const pg = d.pages[key] || (d.pages[key] = {});
    pg.name = name;
  });

  // ---- labels ----
  const labelState = (label) => {
    if (!selectedItems.length) return "none";
    const n = selectedItems.filter((i) => (i.labels || []).includes(label)).length;
    return n === 0 ? "none" : n === selectedItems.length ? "all" : "mixed";
  };
  const applyLabel = (label) => mutate((d) => {
    for (const it of d.items) {
      if (!selection.includes(it.id)) continue;
      const labels = new Set(it.labels || []);
      if (label === "PVC") labels.delete("Aluminum");
      if (label === "Aluminum") labels.delete("PVC");
      labels.add(label);
      it.labels = [...labels];
    }
  });
  // One-tap jacket per pipe row: sets PVC / Aluminum / bare on EVERY measured
  // run of that palette item across the job (that's how a whole run is
  // repriced at once — aluminum = 3× PVC, PVC = grid, bare = base rate).
  const jacketOf = (tagId) => {
    const runs = (doc?.items || []).filter((x) => x.tagId === tagId && x.type === "linear");
    if (!runs.length) return { state: "none", runs: 0 };
    const n = (lbl) => runs.filter((x) => (x.labels || []).includes(lbl)).length;
    const pvc = n("PVC"), al = n("Aluminum");
    const state = al === runs.length ? "Aluminum" : pvc === runs.length ? "PVC" : (al || pvc) ? "mixed" : "bare";
    return { state, runs: runs.length };
  };
  const setJacket = (tagId, jacket) => mutate((d) => {
    for (const it of d.items) {
      if (it.tagId !== tagId || it.type !== "linear") continue;
      const labels = new Set((it.labels || []).filter((l) => l !== "PVC" && l !== "Aluminum"));
      if (jacket) labels.add(jacket);
      it.labels = [...labels];
    }
  });
  const clearLabels = () => mutate((d) => {
    for (const it of d.items) if (selection.includes(it.id)) it.labels = [];
  });
  const addCustomLabel = () => {
    const lbl = labelInput.trim();
    if (!lbl) return;
    mutate((d) => {
      if (!d.jobLabels.includes(lbl)) d.jobLabels.push(lbl);
      for (const it of d.items) {
        if (!selection.includes(it.id)) continue;
        if (!(it.labels || []).includes(lbl)) it.labels = [...(it.labels || []), lbl];
      }
    });
    setLabelInput("");
  };

  const applyMultiplier = () => {
    const m = Math.max(1, Math.round(Number(mult) || 1));
    mutate((d) => { for (const it of d.items) if (selection.includes(it.id)) it.multiplier = m; });
    setMult("");
  };

  const allPageSelected = pageItems.length > 0 && pageItems.every((i) => selection.includes(i.id));
  const somePageSelected = pageItems.some((i) => selection.includes(i.id));

  if (!drawing || !measurable) {
    return (
      <div className="p-4">
        <div className={`${CARD} p-4 text-center`} data-testid="takeoff-panel-empty">
          <p className="text-sm font-semibold text-slate-700">{drawing ? "This file can't be measured" : "No sheet open"}</p>
          <p className="mt-1 text-xs text-slate-500">
            {drawing
              ? "Takeoffs work on PDFs and previewable images. CAD/BIM and office documents are stored for download — export them to PDF to measure."
              : "Open a PDF or image from the Plans tab to start measuring."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="takeoff-panel">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {/* 1 — sheet name */}
        <div className={`${CARD} p-3`}>
          <label className={LABEL}>Sheet name</label>
          <input value={pageState.name} onChange={(e) => setPageName(e.target.value)} className={INPUT_SM} data-testid="takeoff-sheet-name" />
        </div>

        {/* 2 — page scale */}
        <div className={`${CARD} p-3`} data-testid="takeoff-scale-card">
          <p className={LABEL}>Page scale</p>
          <p className={`mb-2 text-xs font-semibold ${pageState.feetPerUnit ? "text-emerald-600" : "text-amber-600"}`}>{pageState.scaleLabel}</p>
          <select
            value=""
            onChange={(e) => {
              const s = STANDARD_SCALES.find((x) => x.value === e.target.value);
              if (s) applyPreset(s.ratio, s.label);
            }}
            className={INPUT_SM}
            data-testid="takeoff-scale-preset"
            aria-label="Choose a standard scale"
          >
            <option value="">Choose a standard scale…</option>
            <optgroup label="Imperial">
              {STANDARD_SCALES.filter((s) => s.group === "Imperial").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </optgroup>
            <optgroup label="Metric">
              {STANDARD_SCALES.filter((s) => s.group === "Metric").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </optgroup>
          </select>
          <button onClick={startCustomScale} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="takeoff-custom-scale-btn">
            <Ruler className="h-3.5 w-3.5" /> Measure a known dimension
          </button>
          <p className="mt-2 text-[10px] leading-snug text-slate-400">Changing scale with takeoffs selected recalculates their quantities. Select all on the page to re-price everything.</p>
        </div>

        {/* 3 — job takeoff items */}
        <div className={`${CARD} p-3`} data-testid="takeoff-job-items">
          <p className={LABEL}>Job takeoff items</p>
          <div className="space-y-1">
            {sortedItems.map((it) => (
              <div key={it.item_id}
                className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${activeItemId === it.item_id ? "border-[#f97316] bg-orange-50" : "border-slate-100 hover:border-slate-200"}`}
                data-testid={`job-item-${it.item_id}`}>
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: it.color }} />
                <button className="min-w-0 flex-1 truncate text-left text-xs font-medium text-slate-800" onClick={() => onPickItem(it.item_id)} title="Activate this measuring tool" data-testid={`job-item-pick-${it.item_id}`}>
                  {it.name}
                </button>
                {tagTotalText(tagTotals[it.item_id]) && (
                  <span className={`${MONO} shrink-0 whitespace-nowrap text-[10px] font-semibold text-slate-500`} title="Job-wide total across all sheets" data-testid={`job-item-total-${it.item_id}`}>
                    {tagTotalText(tagTotals[it.item_id])}
                  </span>
                )}
                {!viewer && it.category === "Pipe" && it.tool !== "count" && (() => {
                  const j = jacketOf(it.item_id);
                  if (!j.runs) return null;
                  const opt = (key, lbl, title) => (
                    <button key={key} type="button" onClick={() => setJacket(it.item_id, key === "bare" ? null : key)}
                      title={`${title} — every measured run of this item on the job (${j.runs})`}
                      aria-pressed={j.state === key}
                      data-testid={`job-item-jacket-${key.toLowerCase()}-${it.item_id}`}
                      className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${j.state === key ? "bg-[#0b172a] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                      {lbl}
                    </button>
                  );
                  return (
                    <span className={`inline-flex shrink-0 overflow-hidden rounded-md border ${j.state === "mixed" ? "border-amber-400" : "border-slate-200"}`} title={j.state === "mixed" ? "Runs have mixed jackets — tap to make them all the same" : undefined} data-testid={`job-item-jacket-${it.item_id}`}>
                      {opt("bare", "—", "Bare (base rate)")}
                      {opt("PVC", "PVC", "PVC jacket (grid rate)")}
                      {opt("Aluminum", "AL", "Aluminum jacket (3× PVC)")}
                    </span>
                  );
                })()}
                {!viewer && (
                <>
                <button aria-label={`Edit ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600"
                  onClick={() => setEditItem(it)} data-testid={`job-item-edit-${it.item_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button aria-label={`Remove ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                  onClick={() => setRemoveItem(it)} data-testid={`job-item-remove-${it.item_id}`}><X className="h-3.5 w-3.5" /></button>
                </>
                )}
              </div>
            ))}
            {!sortedItems.length && <p className="py-2 text-center text-[11px] text-slate-400">{viewer ? "No items measured on this job yet." : "Add items from the Company Library tab, or build a job-only pipe/duct below."}</p>}
          </div>
          {!viewer && (
          <div className="mt-2 flex gap-1.5">
            <button onClick={() => setPipeOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="takeoff-custom-item-btn">Custom item</button>
            <button onClick={() => setDuctOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="takeoff-add-duct-btn">Add duct</button>
            <button onClick={() => setCustomOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="takeoff-quick-item-btn">Quick item</button>
          </div>
          )}
          {/* 4 — active item indicator */}
          <p className="mt-2 text-[11px] text-slate-500" data-testid="takeoff-active-item">
            Measuring: {activeItem ? <span className="font-semibold text-[#f97316]">{activeItem.name}</span> : <span className="text-slate-400">nothing — pick an item</span>}
          </p>
        </div>

        {/* 5 — measured items + bulk controls */}
        <div className={`${CARD} p-3`} data-testid="takeoff-measured">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-800">{pageState.name} — {pageItems.length} takeoff{pageItems.length === 1 ? "" : "s"}</p>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <input
                type="checkbox"
                checked={allPageSelected}
                ref={(el) => { if (el) el.indeterminate = !allPageSelected && somePageSelected; }}
                onChange={() => setSelection(allPageSelected ? [] : pageItems.map((i) => i.id))}
                className="accent-[#f97316]"
                data-testid="takeoff-select-all-page"
              />
              Select all
            </label>
          </div>
          <p className="mt-0.5 text-[10px] text-slate-400" data-testid="takeoff-selected-count">{selection.length} selected</p>

          {/* built-in labels */}
          <div className="mt-2 flex flex-wrap gap-1">
            {FIXED_LABELS.map((l) => {
              const st = labelState(l);
              return (
                <button key={l} onClick={() => applyLabel(l)} disabled={!selection.length}
                  data-testid={`label-btn-${l.replace(/\s/g, "-")}`}
                  aria-pressed={st === "all"}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-40 ${
                    st === "all" ? "border-emerald-400 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-400"
                    : st === "mixed" ? "border-amber-300 bg-amber-50 text-amber-700"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  {st === "all" && <Check className="h-3 w-3" />}
                  {st === "mixed" && <span className="text-[8px]">Mixed</span>}
                  {l}
                </button>
              );
            })}
            <button onClick={clearLabels} disabled={!selection.length} className="rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:bg-slate-50 disabled:opacity-40" data-testid="label-clear">Clear</button>
          </div>

          {/* custom labels */}
          <div className="mt-2 flex gap-1.5">
            <input value={labelInput} onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustomLabel()}
              placeholder="Building A, Room 101…" className={`${INPUT_SM} flex-1`} data-testid="takeoff-custom-label-input" />
            <button onClick={addCustomLabel} disabled={!labelInput.trim() || !selection.length} className="rounded-lg border border-slate-300 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50 disabled:opacity-40" data-testid="takeoff-add-label-btn">Add label</button>
          </div>

          {/* persistent job labels */}
          {(doc.jobLabels || []).length > 0 && (
            <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 p-2" data-testid="takeoff-job-labels">
              <p className="mb-1 text-[9px] font-semibold uppercase tracking-widest text-slate-400">Job labels</p>
              <div className="flex flex-wrap gap-1">
                {doc.jobLabels.map((l) => (
                  <button key={l} onClick={() => applyLabel(l)} disabled={!selection.length}
                    className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:border-[#f97316] hover:text-[#f97316] disabled:opacity-40"
                    data-testid={`job-label-${l.replace(/\s/g, "-")}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* multiplier */}
          <div className="mt-2 flex items-center gap-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Multiplier</label>
            <input type="number" min="1" step="1" value={mult} onChange={(e) => setMult(e.target.value)} placeholder="1"
              className={`${INPUT_SM} w-20`} data-testid="takeoff-multiplier-input" />
            <button onClick={applyMultiplier} disabled={!selection.length || !(Number(mult) >= 1)} className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50 disabled:opacity-40" data-testid="takeoff-multiplier-apply">Apply</button>
          </div>

          {/* 6 — measured item cards */}
          <div className="mt-2 space-y-1.5">
            {pageItems.map((it) => {
              const multN = Math.max(1, Math.round(it.multiplier || 1));
              const total = lineValue(it, overrides);
              const qty = (it.quantity || 0) * multN;
              return (
                <div key={it.id} className={`rounded-lg border p-2 ${selection.includes(it.id) ? "border-[#f97316] bg-orange-50/50" : "border-slate-100"}`} data-testid={`measured-card-${it.id}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={selection.includes(it.id)}
                      onChange={() => setSelection(selection.includes(it.id) ? selection.filter((x) => x !== it.id) : [...selection, it.id])}
                      className="accent-[#f97316]" aria-label={`Select ${it.name}`} data-testid={`measured-select-${it.id}`} />
                    <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: it.color }} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-800">{it.name}</p>
                      <p className="text-[10px] text-slate-400">
                        {it.type}
                        {(it.labels || []).map((l) => <span key={l} className="ml-1 rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-semibold text-slate-500">{l}</span>)}
                      </p>
                    </div>
                    {!viewer && (
                    <button aria-label={`Delete ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                      onClick={() => mutate((d) => { d.items = d.items.filter((x) => x.id !== it.id); })}
                      data-testid={`measured-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-between pl-6">
                    <p className={`${MONO} text-[11px] text-slate-600`}>
                      {fmtQty(qty)} {it.unit}
                      {multN > 1 && <span className="ml-1 rounded bg-[#f97316]/10 px-1 text-[9px] font-bold text-[#f97316]">×{multN}</span>}
                      {it.ductSpec && it.type === "linear" && <span className="ml-1 text-slate-400">→ {fmtQty(ductSqFt(it) * multN)} sq ft</span>}
                    </p>
                    {!viewer && <p className={`${MONO} text-[11px] font-bold text-slate-900`}>{money(total)}</p>}
                  </div>
                </div>
              );
            })}
            {!pageItems.length && <p className="py-3 text-center text-[11px] text-slate-400">Nothing measured on this sheet yet.</p>}
          </div>
        </div>

        {/* 7 — addendum overlay */}
        <OverlayPanel drawing={drawing} pageNum={pageNum} overlay={overlay} setOverlay={setOverlay} pageText={pageText} />
      </div>

      {/* 8 — sticky total footer (hidden for viewers: it's the job's price) */}
      {!viewer && (
      <div className="shrink-0 border-t border-slate-200 bg-[#0b172a] px-4 py-3" data-testid="takeoff-total-footer">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-white/50">Job estimate total</span>
          <span className={`${MONO} text-lg font-bold text-[#fb923c]`} data-testid="takeoff-job-total">{money(estimate?.total || 0)}</span>
        </div>
      </div>
      )}

      {/* item builder dialogs */}
      <PipeBuilderDialog open={pipeOpen} onClose={() => setPipeOpen(false)} library={library} onSave={(item) => addJobItem(item)} />
      <DuctBuilderDialog open={ductOpen} onClose={() => setDuctOpen(false)} onSave={(item) => addJobItem(item)} />
      <CustomItemDialog open={customOpen || (!!editItem && isCustomItem(editItem))} extras={doc?.extras || []}
        editItem={customOpen ? null : editItem}
        onClose={() => { setCustomOpen(false); setEditItem(null); }}
        onSave={saveCustomItem} />
      <PipeBuilderDialog open={!!editItem && editItem.category === "Pipe" && !!parseSizeThickness(editItem.name)[0]} onClose={() => setEditItem(null)} library={library}
        editItem={editItem} onSave={(p) => updateJobItem(editItem.item_id, p)} />
      <DuctBuilderDialog open={!!editItem && !!editItem.ductSpec} onClose={() => setEditItem(null)}
        editItem={editItem} onSave={(p) => updateJobItem(editItem.item_id, p)} />
      <LibraryItemDialog open={!!editItem && !isCustomItem(editItem) && !editItem.ductSpec && !parseSizeThickness(editItem.name || "")[0]} onClose={() => setEditItem(null)}
        item={editItem} onSave={(p) => updateJobItem(editItem.item_id, p)} />
      <ConfirmDialog open={!!removeItem} onClose={() => setRemoveItem(null)}
        title={`Remove ${removeItem?.name}?`}
        body="The item leaves this job's palette. Measurements already drawn with it stay on the drawings."
        confirmLabel="Remove"
        onConfirm={() => removeJobItem(removeItem.item_id)} />
    </div>
  );
}
